import axiosInstance from "./api";

/**
 * Account alerts — the notices support staff write when they have had to change
 * something on a customer's account or on one of their jobs ("we swapped the
 * proxy pool on order #4120", "your card was declined so the campaign paused").
 *
 * Its own service, alongside notification-preferences.service.js and for the
 * same reason: `axiosInstance` carries the bearer token AND the refresh
 * interceptor, whereas the bare `axios` some older services use does not — a
 * token expiring mid-poll would sign the customer out rather than retry.
 *
 * The account is derived from the token, so no user id is passed from here.
 *
 * GET  /user/alerts
 *   {
 *     alerts: [{
 *       id, severity,            // 'info' | 'warning' | 'action_required'
 *       title, body,
 *       order_id, order_label,   // the job this is about, when it is about one
 *       action_label, action_url,
 *       allow_chat,              // may this be taken to support chat?
 *       created_at, read_at
 *     }],
 *     has_more: true,            // is there an older page behind this one?
 *     available: true
 *   }
 *
 *   The default call returns the ten most recent, newest first. Dismissed ones
 *   are excluded; ones already READ are included — the bell is a history, not
 *   an inbox that empties itself as you look at it.
 *
 *   `available: false` (with an empty list) means the backend has nowhere to
 *   store alerts yet — the table is missing. That is an ABSENT answer, not a
 *   failure and not "you have no alerts": in that state the bell does not
 *   appear at all. Same treatment of `available` as in
 *   notification-preferences.service.js.
 *
 * GET  /user/alerts?before=<id>&limit=10
 *   The next older page, for "View previous". A cursor rather than a page
 *   number because alerts arrive while the dropdown is open: with `?page=2` a
 *   new alert at the top shifts everything down one and the second page repeats
 *   the row the first page ended on. `before` is anchored to a row the reader
 *   is already looking at, so nothing can be repeated or skipped.
 *
 * POST /user/alerts/read     { id } -> { ok: true }
 * POST /user/alerts/dismiss  { id } -> { ok: true }
 *   Both take the alert id in the BODY rather than the path, because that is
 *   the contract the backend is being built to.
 */
const PATH = "/user/alerts";

/** One dropdown-sized page. Also the server's default when `limit` is absent. */
export const ALERTS_PAGE_SIZE = 10;

class AlertsService {
  /**
   * A page of alerts, newest first.
   *
   * @param {{before?: number, limit?: number}} [options]
   *   `before` is the id of the oldest row already on screen; the server
   *   answers with the ones older than it. Omit it for the first page.
   */
  get(options = {}) {
    const params = {};
    if (options.before !== undefined && options.before !== null) {
      params.before = options.before;
    }
    if (options.limit) params.limit = options.limit;

    // No `params` key at all for the first page, so the request is byte-for-byte
    // the plain `GET /user/alerts` the contract specifies.
    return Object.keys(params).length
      ? axiosInstance.get(PATH, { params })
      : axiosInstance.get(PATH);
  }

  /**
   * Mark one alert as seen. Fire-and-forget from the caller's point of view —
   * a failed read-receipt costs the customer nothing, so nothing is shown.
   * @param {number} id
   */
  markRead(id) {
    return axiosInstance.post(`${PATH}/read`, { id });
  }

  /**
   * Remove one alert from the customer's panel for good. Unlike `markRead`,
   * this one's failure MUST reach the caller: an alert that appears to be gone
   * and comes back on the next reload is worse than one that never went.
   * @param {number} id
   */
  dismiss(id) {
    return axiosInstance.post(`${PATH}/dismiss`, { id });
  }
}

export default new AlertsService();
